import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Target, DollarSign, Globe, BarChart3, Save, CheckCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  'Energy / Oil & Gas', 'Minerals', 'Other Industrial'
];

const REGIONS = [
  'Algeria', 'Angola', 'Bahrain', 'Benin', 'Botswana', 'Burkina Faso',
  'Cameroon', 'Chad', 'Comoros', 'Congo', 'DRC', 'Djibouti', 'Egypt',
  'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon',
  'Gambia', 'Ghana', 'Guinea', 'Iraq', 'Iran', 'Israel', 'Jordan',
  'Kenya', 'Kuwait', 'Lebanon', 'Lesotho', 'Libya', 'Madagascar',
  'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco', 'Mozambique',
  'Namibia', 'Niger', 'Nigeria', 'Oman', 'Palestine', 'Qatar', 'Rwanda',
  'Saudi Arabia', 'Senegal', 'Sierra Leone', 'Somalia', 'South Africa',
  'South Sudan', 'Sudan', 'Syria', 'Tanzania', 'Togo', 'Tunisia', 'Turkey',
  'UAE', 'Uganda', 'Yemen', 'Zambia', 'Zimbabwe'
].sort();

const COMMODITIES = [
  'Aluminum', 'Antimony', 'Bauxite', 'Beryllium', 'Bismuth',
  'Cadmium', 'Chromium', 'Coal', 'Cobalt', 'Coltan',
  'Copper', 'Diamond', 'Fluorspar', 'Gallium', 'Germanium',
  'Gold', 'Graphite', 'Hafnium', 'Ilmenite', 'Indium',
  'Iridium', 'Iron Ore', 'Lead', 'Lithium', 'Magnesium',
  'Manganese', 'Mercury', 'Molybdenum', 'Nickel', 'Niobium',
  'Palladium', 'Phosphate', 'Platinum', 'Potash',
  'Rare Earth Elements', 'Rhenium', 'Rhodium', 'Ruthenium',
  'Scandium', 'Selenium', 'Silicon', 'Silver',
  'Tantalum', 'Tellurium', 'Tin', 'Titanium', 'Tungsten',
  'Uranium', 'Vanadium', 'Zinc', 'Zirconium'
];

export function InvestmentPreferencesPage() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    min_ticket_size: '',
    max_ticket_size: '',
    preferred_geography: [],
    sector_focus: [],
    commodity_interest: [],
    risk_appetite: 'moderate'
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/preferences`, { headers: getAuthHeaders() });
      if (res.data && Object.keys(res.data).length > 0) {
        setPrefs({
          min_ticket_size: res.data.min_ticket_size || '',
          max_ticket_size: res.data.max_ticket_size || '',
          preferred_geography: res.data.preferred_geography || [],
          sector_focus: res.data.sector_focus || [],
          commodity_interest: res.data.commodity_interest || [],
          risk_appetite: res.data.risk_appetite || 'moderate'
        });
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const payload = {
        ...prefs,
        min_ticket_size: prefs.min_ticket_size ? Number(prefs.min_ticket_size) : null,
        max_ticket_size: prefs.max_ticket_size ? Number(prefs.max_ticket_size) : null
      };
      await axios.patch(`${API}/auth/me/preferences`, payload, { headers: getAuthHeaders() });
      toast.success('Investment preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (field, item) => {
    setPrefs(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="investment-preferences-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Investment Preferences</h1>
        <p className="text-muted-foreground">Define your investment criteria to receive AI-powered deal recommendations</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <DollarSign size={18} />
            <CardTitle className="text-base">Ticket Size Range</CardTitle>
          </div>
          <CardDescription>Set your minimum and maximum investment amount (USD)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Minimum Ticket Size</Label>
              <Input
                type="number"
                placeholder="e.g. 1000000"
                value={prefs.min_ticket_size}
                onChange={(e) => setPrefs({ ...prefs, min_ticket_size: e.target.value })}
                data-testid="input-min-ticket"
              />
              {prefs.min_ticket_size && (
                <p className="text-xs text-muted-foreground mt-1 font-mono">{formatCurrency(Number(prefs.min_ticket_size))}</p>
              )}
            </div>
            <div>
              <Label>Maximum Ticket Size</Label>
              <Input
                type="number"
                placeholder="e.g. 50000000"
                value={prefs.max_ticket_size}
                onChange={(e) => setPrefs({ ...prefs, max_ticket_size: e.target.value })}
                data-testid="input-max-ticket"
              />
              {prefs.max_ticket_size && (
                <p className="text-xs text-muted-foreground mt-1 font-mono">{formatCurrency(Number(prefs.max_ticket_size))}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart3 size={18} />
            <CardTitle className="text-base">Risk Appetite</CardTitle>
          </div>
          <CardDescription>Your tolerance for investment risk</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={prefs.risk_appetite} onValueChange={(v) => setPrefs({ ...prefs, risk_appetite: v })}>
            <SelectTrigger data-testid="select-risk-appetite">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="conservative">Conservative — Operational & construction stage, lower returns</SelectItem>
              <SelectItem value="moderate">Moderate — Development stage, balanced risk-return</SelectItem>
              <SelectItem value="aggressive">Aggressive — Early stage, higher potential returns</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target size={18} />
            <CardTitle className="text-base">Sector Focus</CardTitle>
          </div>
          <CardDescription>Select sectors you are interested in ({prefs.sector_focus.length} selected)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {SECTORS.map(sector => (
              <Badge
                key={sector}
                variant={prefs.sector_focus.includes(sector) ? 'default' : 'outline'}
                className="cursor-pointer select-none transition-colors"
                onClick={() => toggleItem('sector_focus', sector)}
                data-testid={`sector-${sector.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {prefs.sector_focus.includes(sector) && <CheckCircle size={12} className="mr-1" />}
                {sector}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe size={18} />
            <CardTitle className="text-base">Geographic Focus</CardTitle>
          </div>
          <CardDescription>Select countries of interest ({prefs.preferred_geography.length} selected)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {REGIONS.map(country => (
              <Badge
                key={country}
                variant={prefs.preferred_geography.includes(country) ? 'default' : 'outline'}
                className="cursor-pointer select-none transition-colors text-xs"
                onClick={() => toggleItem('preferred_geography', country)}
                data-testid={`geo-${country.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {prefs.preferred_geography.includes(country) && <CheckCircle size={10} className="mr-0.5" />}
                {country}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target size={18} />
            <CardTitle className="text-base">Commodity Interest</CardTitle>
          </div>
          <CardDescription>Select commodities of interest ({prefs.commodity_interest.length} selected)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {COMMODITIES.map(commodity => (
              <Badge
                key={commodity}
                variant={prefs.commodity_interest.includes(commodity) ? 'default' : 'outline'}
                className="cursor-pointer select-none transition-colors"
                onClick={() => toggleItem('commodity_interest', commodity)}
                data-testid={`commodity-${commodity.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {prefs.commodity_interest.includes(commodity) && <CheckCircle size={12} className="mr-1" />}
                {commodity}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div className="flex justify-end">
        <Button onClick={savePreferences} disabled={saving} size="lg" data-testid="save-preferences-btn">
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
